import React, { useEffect, useState, useCallback } from 'react';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import type {
  PaymentRequest as StripePaymentRequest,
  PaymentRequestPaymentMethodEvent,
  CanMakePaymentResult,
} from '@stripe/stripe-js';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { paymentGatewayApi } from '@/services/paymentGatewayApi';

export interface StripeWalletButtonProps {
  /** Amount in major units (e.g. 29.00). */
  amount: number;
  currency: string;
  /** Display label shown in the wallet sheet ("Five AI Pro"). */
  label: string;
  /** Country code (Stripe requires the merchant country, not the customer's). */
  country?: string;
  customerEmail?: string;
  customerId?: string;
  subscriptionId?: string;
  planId?: string;
  invoiceId?: string;
  description?: string;
  onPaymentSucceeded: (paymentIntentId: string) => void;
  onPaymentFailed: (message: string) => void;
}

/**
 * Renders Apple Pay (Safari/iOS) **and** Google Pay (Chrome/Android) via Stripe's
 * Payment Request API — a single button that adapts to whichever wallet the browser
 * supports. Hidden entirely when no wallet is available.
 *
 * <p>Why one component for both: {@code stripe.paymentRequest()} performs feature
 * detection and returns {@code canMakePayment().applePay} or {@code .googlePay}; the
 * {@code PaymentRequestButtonElement} renders the correct native button automatically.
 * Separate components would duplicate plumbing for no reason.</p>
 */
export const StripeWalletButton: React.FC<StripeWalletButtonProps> = ({
  amount,
  currency,
  label,
  country = 'US',
  customerEmail,
  customerId,
  subscriptionId,
  planId,
  invoiceId,
  description,
  onPaymentSucceeded,
  onPaymentFailed,
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<StripePaymentRequest | null>(null);
  const [walletKind, setWalletKind] = useState<'apple' | 'google' | 'browser' | null>(null);

  // Build the PaymentRequest once Stripe is ready. Re-build when amount/currency change.
  useEffect(() => {
    if (!stripe || amount <= 0) return;
    const pr = stripe.paymentRequest({
      country,
      currency: currency.toLowerCase(),
      total: {
        label,
        amount: Math.round(amount * 100), // smallest unit
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result: CanMakePaymentResult | null) => {
      if (!result) {
        setPaymentRequest(null);
        return;
      }
      setPaymentRequest(pr);
      if (result.applePay) setWalletKind('apple');
      else if (result.googlePay) setWalletKind('google');
      else setWalletKind('browser');
    });
  }, [stripe, amount, currency, label, country]);

  const handlePaymentMethod = useCallback(
    async (event: PaymentRequestPaymentMethodEvent) => {
      try {
        const intent = await paymentGatewayApi.createPaymentIntent({
          amount,
          currency,
          customerEmail: customerEmail ?? event.payerEmail,
          customerId,
          subscriptionId,
          planId,
          invoiceId,
          description,
          gateway: 'STRIPE',
        });
        if (!stripe || !intent.clientSecret) {
          event.complete('fail');
          onPaymentFailed('Payment intent has no client secret.');
          return;
        }

        // Confirm payment without re-handling 3DS — Stripe's wallet flow
        // already requires biometric or device auth, so this is usually no-op SCA.
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          intent.clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false },
        );

        if (error) {
          event.complete('fail');
          onPaymentFailed(error.message ?? 'Wallet payment failed');
          return;
        }
        event.complete('success');

        // If the PaymentIntent still requires action (rare for wallets), drive next-action.
        if (paymentIntent && paymentIntent.status === 'requires_action') {
          const followup = await stripe.confirmCardPayment(intent.clientSecret);
          if (followup.error) {
            onPaymentFailed(followup.error.message ?? 'Payment authentication failed');
            return;
          }
          if (followup.paymentIntent && followup.paymentIntent.status === 'succeeded') {
            onPaymentSucceeded(followup.paymentIntent.id);
          }
          return;
        }
        if (paymentIntent && paymentIntent.status === 'succeeded') {
          onPaymentSucceeded(paymentIntent.id);
        }
      } catch (e) {
        event.complete('fail');
        onPaymentFailed(e instanceof Error ? e.message : 'Wallet payment failed');
      }
    },
    [stripe, amount, currency, customerEmail, customerId, subscriptionId, planId, invoiceId, description, onPaymentSucceeded, onPaymentFailed],
  );

  // Bind the event handler — must use the addEventListener-style API on PaymentRequest.
  useEffect(() => {
    if (!paymentRequest) return;
    paymentRequest.on('paymentmethod', handlePaymentMethod);
    return () => {
      paymentRequest.off('paymentmethod', handlePaymentMethod);
    };
  }, [paymentRequest, handlePaymentMethod]);

  if (!paymentRequest) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center">
        <p className="text-sm text-muted-foreground">
          No digital wallets detected for this device.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Use a card or try Chrome (Google Pay) or Safari on Apple devices (Apple Pay).
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-3"
    >
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: 'default',
              theme: 'dark',
              height: '48px',
            },
          },
        }}
      />
      <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
        <Shield className="h-3 w-3" />
        {walletKind === 'apple' && 'Authenticate with Face ID or Touch ID'}
        {walletKind === 'google' && 'Confirm with your Google account'}
        {walletKind === 'browser' && 'Confirm with your saved browser payment method'}
      </p>
    </motion.div>
  );
};
